/**
 * PrimaryActionZone.tsx — WEB.4.UI.4
 *
 * Center-bottom fixed slot. Single button that swaps content based on turnPhase:
 *   - 'idle'   → Roll Dice (solar-amber, spinning dice glyph, amber-pulse loop)
 *   - 'landed' → Leave Tile (energy-green, door glyph) + last-roll memento
 *
 * Leave Tile = endTurn. Also closes BuildMenu if open (via closeBuildMenu → endTurn).
 *
 * Z-index: 50 — above HandPanel collapse-tab, below modal scrim (z:200).
 * Single DOM node; does NOT render two buttons and hide one (a11y focus order).
 *
 * VISIBLE WHEN: always (floats at bottom of viewport).
 */

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAudio } from "../../audio/useAudio.js";
import { useI18n } from "../../i18n/useI18n.js";
import { useGameStore } from "../../store/game-store.js";
import { TILE_POSITIONS } from "../HexBoard/index.js";
import styles from "./PrimaryActionZone.module.css";

// R9 #11 — Tile types that require a card draw before leaving.
// Event → newsgirl-e1.png   Policy → bureaucrat-e1.png
const DRAW_REQUIRED_TILE_TYPES = new Set(["Event", "Policy"]);
const DRAW_NPC_FILE: Record<string, string> = {
  Event: "newsgirl-e1.png",
  Policy: "bureaucrat-e1.png",
};

// R15-AS: die face glyphs ⚀–⚅ (Unicode U+2680–U+2685).
// Module-level constant to avoid recreation per render.
const DIE_FACES = ["⚀", "⚁", "⚂", "⚃", "⚄", "⚅"] as const;

// Bug 4+9: cycle 1-6 on the Roll Dice button during a ~1100ms animation
// window before actually rolling. Digit is shown large + gold (Bug 4).
// After rollDice() fires, a RollResultBanner stays up for 1200ms (Bug 9).
// Honors prefers-reduced-motion (skip the animation, roll immediately).
const ROLL_ANIM_DURATION_MS = 1100;
// R15-AS: 80ms tick (≈14 face changes/s) — fast enough for "tumbling dice" feel.
const ROLL_ANIM_TICK_MS = 80;
const ROLL_BANNER_DURATION_MS = 1200; // how long the result banner lingers

export function PrimaryActionZone(): React.ReactElement {
  const { t } = useI18n();
  const turnPhase = useGameStore((s) => s.turnPhase);
  // R11.2 — board walk gate. Suppress "Leave Tile" / "Wait in Jail" and the
  // last-roll memento while the seat token is still walking to its tile.
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const lastDiceRoll = useGameStore((s) => s.lastDiceRoll);
  const rollDice = useGameStore((s) => s.rollDice);
  const endTurn = useGameStore((s) => s.endTurn);
  const closeBuildMenu = useGameStore((s) => s.closeBuildMenu);
  const requestLeaveScene = useGameStore((s) => s.requestLeaveScene);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const jailTurnsRemaining = useGameStore((s) => s.jailTurnsRemaining);
  const isJailed = (jailTurnsRemaining[currentSeat] ?? 0) > 0;
  // R9 #11 — leave-tile gate: read current tile + NPC interaction state
  const playerTiles = useGameStore((s) => s.playerTiles);
  const npcInteractionState = useGameStore((s) => s.npcInteractionState);
  const { play } = useAudio();

  // R9 #11 — transient "must draw first" toast (local — no store overhead)
  const [drawRequiredMsg, setDrawRequiredMsg] = useState<string | null>(null);
  const drawRequiredTimerRef = useRef<number | null>(null);

  // Bug 4+9 — animation state. animNum is the currently-displayed digit
  // (cycles 1-6 during the animation window). null = no animation in flight.
  const [animNum, setAnimNum] = useState<number | null>(null);
  const animIntervalRef = useRef<number | null>(null);
  const animTimeoutRef = useRef<number | null>(null);

  // Bug 9 — roll result banner. bannerRoll holds the just-rolled value while
  // the banner is visible (1200ms). null = banner hidden.
  const [bannerRoll, setBannerRoll] = useState<number | null>(null);
  const bannerTimeoutRef = useRef<number | null>(null);

  // Bug 4 — floating dice overlay (separate from in-button glyph).
  const [overlayNum, setOverlayNum] = useState<number | null>(null);
  const [overlayFading, setOverlayFading] = useState(false);
  const overlayFadeTimeoutRef = useRef<number | null>(null);

  const stopAnim = useCallback(() => {
    if (animIntervalRef.current !== null) {
      window.clearInterval(animIntervalRef.current);
      animIntervalRef.current = null;
    }
    if (animTimeoutRef.current !== null) {
      window.clearTimeout(animTimeoutRef.current);
      animTimeoutRef.current = null;
    }
    setAnimNum(null);
    // Bug 4: begin overlay fade-out when cycle ends
    setOverlayFading(true);
    if (overlayFadeTimeoutRef.current !== null) {
      window.clearTimeout(overlayFadeTimeoutRef.current);
    }
    overlayFadeTimeoutRef.current = window.setTimeout(() => {
      setOverlayNum(null);
      setOverlayFading(false);
      overlayFadeTimeoutRef.current = null;
    }, 200);
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      stopAnim();
      if (bannerTimeoutRef.current !== null) {
        window.clearTimeout(bannerTimeoutRef.current);
      }
      if (overlayFadeTimeoutRef.current !== null) {
        window.clearTimeout(overlayFadeTimeoutRef.current);
      }
      // R9 #11 — clean up draw-required timer
      if (drawRequiredTimerRef.current !== null) {
        window.clearTimeout(drawRequiredTimerRef.current);
      }
    };
  }, [stopAnim]);

  // MED fix (Codex R2): extract rollAndShowBanner so both animated and
  // reduced-motion paths call rollDice() AND show the RollResultBanner.
  // The banner respects reduced-motion via CSS (snap-in vs fade-in) but MUST
  // appear for both groups so all players see what they rolled (bug 9 fix).
  // aria-live="polite" on the banner element ensures screen readers announce it.
  function rollAndShowBanner(): void {
    rollDice();
    // Read roll synchronously from zustand after the dispatch (sync setter).
    const roll = useGameStore.getState().lastDiceRoll;
    if (roll !== null) {
      setBannerRoll(roll);
      if (bannerTimeoutRef.current !== null) {
        window.clearTimeout(bannerTimeoutRef.current);
      }
      bannerTimeoutRef.current = window.setTimeout(() => {
        setBannerRoll(null);
        bannerTimeoutRef.current = null;
      }, ROLL_BANNER_DURATION_MS);
    }
  }

  function handleRoll(): void {
    play("dice-roll");
    // Honor prefers-reduced-motion: skip the cycling animation but still show banner.
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || animIntervalRef.current !== null) {
      // Already animating (button mashing) OR reduced-motion → roll + banner, no anim.
      rollAndShowBanner();
      return;
    }
    // Start cycling — sync both in-button glyph and floating overlay
    const firstNum = 1 + Math.floor(Math.random() * 6);
    setAnimNum(firstNum);
    setOverlayNum(firstNum);
    setOverlayFading(false);
    animIntervalRef.current = window.setInterval(() => {
      const n = 1 + Math.floor(Math.random() * 6);
      setAnimNum(n);
      setOverlayNum(n);
    }, ROLL_ANIM_TICK_MS);
    // Settle after duration → fire rollDice → show banner
    animTimeoutRef.current = window.setTimeout(() => {
      stopAnim();
      rollAndShowBanner();
    }, ROLL_ANIM_DURATION_MS);
  }

  function handleLeaveTile(): void {
    // R9 #11 — Gate: Event/Policy tiles require NPC card draw before leaving.
    const tileIdx = playerTiles[currentSeat] ?? 0;
    const tileType = TILE_POSITIONS[tileIdx]?.type ?? "";
    if (DRAW_REQUIRED_TILE_TYPES.has(tileType) && !isJailed) {
      const npcFile = DRAW_NPC_FILE[tileType];
      const key = `${tileIdx}:${npcFile}`;
      const npcDone = npcInteractionState[key] === "completed";
      if (!npcDone) {
        // Block leave — show 2.5s toast
        const label = tileType === "Event" ? "Event" : "Policy";
        const msg = `Draw the ${label} card first!`;
        setDrawRequiredMsg(msg);
        if (drawRequiredTimerRef.current !== null) {
          window.clearTimeout(drawRequiredTimerRef.current);
        }
        drawRequiredTimerRef.current = window.setTimeout(() => {
          setDrawRequiredMsg(null);
          drawRequiredTimerRef.current = null;
        }, 2500);
        return; // ← gate: do NOT leave
      }
    }

    // Close build menu first (user skipped build by pressing Leave Tile)
    closeBuildMenu();
    // WEB.4.SS.5: request scene leave — SidescrollerScene plays walk-out
    // animation then calls endTurn() itself. If sidescroller isn't mounted
    // (jail / no landed scene), the request is a no-op and we fall through
    // to direct endTurn after a brief tick so PrimaryActionZone behavior
    // is consistent. Simpler: always request, and after a small delay if
    // still landed, force-end (defensive). For now: just request — scene
    // will end-turn within ~1.5s. Falls through to endTurn if no scene.
    if (turnPhase === "landed" && !isJailed) {
      requestLeaveScene();
    } else {
      // Jailed or not in landed phase — direct endTurn (no walk-out anim)
      endTurn();
    }
  }

  const isIdle = turnPhase === "idle";
  // R11.2: treat "landed-during-walk" as not-landed visually. After the token
  // arrives, boardAnimationActive flips false and the tile buttons surface.
  const isLanded = turnPhase === "landed" && !boardAnimationActive;

  const btnClass = [styles.primaryBtn, isLanded ? styles.primaryBtnLeave : ""]
    .filter(Boolean)
    .join(" ");

  // Bug 9: roll result banner — shows for ROLL_BANNER_DURATION_MS after rollDice fires.
  // Positioned fixed above the button via CSS; pointer-events none so it doesn't block.
  const rollBannerEl =
    bannerRoll !== null ? (
      <div className={styles.rollBanner} data-testid="roll-result-banner" aria-live="polite">
        P{currentSeat + 1} ROLLED
        <span className={styles.rollBannerNum} data-testid="roll-result-banner-num">
          {bannerRoll}
        </span>
      </div>
    ) : null;

  // Bug 4: choose glyph class based on animating state
  const glyphClass = animNum !== null ? styles.diceGlyphAnimating : styles.diceGlyph;

  // R15-AS: die face for in-button glyph during tumble (⚀-⚅ instead of raw digit).
  const animDieFace = animNum !== null ? (DIE_FACES[animNum - 1] ?? "⚀") : null;

  // Bug 4: floating dice overlay element — R15-AS: use die face glyph for visual tumble.
  const overlayDieFace = overlayNum !== null ? (DIE_FACES[overlayNum - 1] ?? "⚀") : null;
  const diceCycleOverlayEl =
    overlayDieFace !== null ? (
      <div
        className={[styles.diceCycleOverlay, overlayFading ? styles.diceCycleOverlayFading : ""]
          .filter(Boolean)
          .join(" ")}
        data-testid="dice-cycle-overlay"
        aria-hidden="true"
      >
        {overlayDieFace}
      </div>
    ) : null;

  return (
    <div className={styles.zone} data-testid="primary-action-zone">
      {diceCycleOverlayEl}
      {rollBannerEl}

      {/* R9 #11 — draw-required toast: blocks leave on Event/Policy tiles */}
      {drawRequiredMsg !== null && (
        <div
          className={styles.drawRequiredToast}
          data-testid="draw-required-toast"
          aria-live="assertive"
          aria-label={drawRequiredMsg}
        >
          ⚠ {drawRequiredMsg}
        </div>
      )}

      {/* Last roll memento — only visible after rolling */}
      {isLanded && lastDiceRoll !== null && (
        <div className={styles.lastRoll} data-testid="last-roll-memento">
          ROLLED
          <span className={styles.lastRollVal}>{lastDiceRoll}</span>
        </div>
      )}

      {/* Single primary button — swaps content based on turnPhase + jail state.
          R11.2: hide the button entirely while the board-walk animation is in
          flight so the in-tile actions don't flash before the token arrives.
          Dice cycle overlay + roll banner above still render (they belong to
          the roll-in-flight beat, not the tile-stage UI). */}
      <div className={styles.btnWrap}>
        {boardAnimationActive ? null : isJailed ? (
          <button
            type="button"
            className={btnClass}
            onClick={handleLeaveTile}
            data-testid="primary-btn-wait-jail"
            title="End turn — jail counter decrements. Use PlayerHUD to pay or attempt escape."
          >
            <span className={styles.leaveGlyph}>⏳</span>
            <span>{t("primaryAction.waitInJail")}</span>
          </button>
        ) : isIdle ? (
          <button
            type="button"
            className={btnClass}
            onClick={handleRoll}
            data-testid="primary-btn-roll-dice"
            disabled={animNum !== null}
          >
            <span className={glyphClass} data-testid="dice-glyph" aria-live="polite">
              {animDieFace !== null ? animDieFace : "🎲"}
            </span>
            <span>{animNum !== null ? t("primaryAction.rolling") : t("buttons.rollDice")}</span>
          </button>
        ) : (
          <button
            type="button"
            className={btnClass}
            onClick={handleLeaveTile}
            data-testid="primary-btn-leave-tile"
          >
            <span className={styles.leaveGlyph}>🚪</span>
            <span>{t("buttons.leaveTile")}</span>
          </button>
        )}
        <div className={styles.caption}>
          {isJailed
            ? "In jail — pay/escape via PlayerHUD or wait it out"
            : `Primary action · ${isIdle ? "turnPhase=idle" : "turnPhase=landed"}`}
        </div>
      </div>
    </div>
  );
}
